/**
 * Redux slice (src/store/slices/evaluationSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

// src/store/slices/evaluationSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  createEvaluation,
  getAllEvaluations,
  getStudentEvaluations,
  getTeacherEvaluations,
  getEvaluation,
  updateEvaluation,
  deleteEvaluation,
  getEvaluationStats,
  normalizeStudentEvaluationError,
  getStudentEvaluationScopeKey,
  // New admin actions
  getAllEvaluationsAdmin,
  searchEvaluations,
  getEvaluationStatsAdmin,
  getEvaluationAnalytics,
  exportEvaluations,
  bulkDeleteEvaluations,
  getAllTrialPreferences,
  updateTrialPreferences,
  deleteTrialPreferences
} from "../actions/evaluationActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const emptyStudentStats = () => ({ averageScore: 0, totalEvaluations: 0, minScore: 0, maxScore: 0 });
const emptyStudentPagination = () => ({ total: 0, currentPage: 1, totalPages: 0, pageSize: 10 });
const learnerScope = getStudentEvaluationScopeKey;
const accessFailure = (error) => ['unauthorized', 'forbidden', 'unavailable'].includes(error?.kind);
const resetLearnerScope = (state, key) => {
  state.studentEvaluationScopeKey = key;
  state.studentEvaluationContextRevision += 1;
  state.evaluations = [];
  state.singleEvaluation = null;
  state.stats = emptyStudentStats();
  state.studentEvaluationPagination = emptyStudentPagination();
  state.studentEvaluationsRequestId = null;
  state.studentEvaluationStatsRequestId = null;
  state.studentEvaluationsLoading = false;
  state.studentEvaluationStatsLoading = false;
  state.studentEvaluationsError = null;
  state.studentEvaluationStatsError = null;
  state.error = null;
  state.loading = false;
};
const syncLearnerLoad = (state) => {
  state.loading = state.studentEvaluationsLoading || state.studentEvaluationStatsLoading;
  const errors = [state.studentEvaluationsError, state.studentEvaluationStatsError];
  state.error = errors.find(accessFailure) || errors.find(Boolean) || null;
};
const beginLearnerLoad = (state, action, stats) => {
  const key = learnerScope(action.meta?.arg);
  if (state.studentEvaluationScopeKey !== key) resetLearnerScope(state, key);
  if (stats) {
    state.studentEvaluationStatsRequestId = action.meta.requestId;
    state.studentEvaluationStatsLoading = true;
    state.studentEvaluationStatsError = null;
  } else {
    state.studentEvaluationsRequestId = action.meta.requestId;
    state.studentEvaluationsLoading = true;
    state.studentEvaluationsError = null;
  }
  syncLearnerLoad(state);
};
const currentLearnerLoad = (state, action, stats) => Boolean(action.meta?.requestId)
  && state.studentEvaluationScopeKey === learnerScope(action.meta?.arg)
  && action.meta.requestId === (stats ? state.studentEvaluationStatsRequestId : state.studentEvaluationsRequestId)
  && (stats ? state.studentEvaluationStatsLoading : state.studentEvaluationsLoading);

const initialState = {
  evaluations: [],
  studentEvaluationPagination: {
    total: 0,
    currentPage: 1,
    totalPages: 0,
    pageSize: 10,
  },
  studentEvaluationScopeKey: null,
  studentEvaluationContextRevision: 0,
  studentEvaluationStatsLoading: false,
  studentEvaluationStatsError: null,
  studentEvaluationsLoading: false,
  studentEvaluationsError: null,
  studentEvaluationsRequestId: null,
  studentEvaluationStatsRequestId: null,
  singleEvaluation: null,
  stats: {
    averageScore: 0,
    totalEvaluations: 0,
    minScore: 0,
    maxScore: 0
  },
  // New admin state
  adminEvaluations: {
    data: [],
    total: 0,
    totalPages: 0,
    currentPage: 1
  },
  searchResults: {
    data: [],
    total: 0,
    totalPages: 0,
    currentPage: 1,
    searchTerm: ''
  },
  adminStats: {
    timeRange: '30d',
    stats: {
      overall: {
        totalEvaluations: 0,
        averageScore: 0,
        minScore: 0,
        maxScore: 0
      },
      topTeachers: [],
      topStudents: [],
      scoreDistribution: [],
      dailyTrend: []
    }
  },
  analytics: {
    scoreRanges: [],
    monthlyTrends: [],
    topPerformers: []
  },
  trialPreferences: {
    data: [],
    total: 0,
    totalPages: 0,
    currentPage: 1
  },
  loading: false,
  adminLoading: false,
  searchLoading: false,
  statsLoading: false,
  analyticsLoading: false,
  exportLoading: false,
  bulkDeleteLoading: false,
  trialLoading: false,
  teacherEvaluationsLoading: false,
  teacherEvaluationsError: null,
  teacherEvaluationsRequestId: null,
  error: null
};

const getThunkErrorMessage = (action, fallbackMessage) => {
  if (typeof action?.payload === 'string' && action.payload.trim()) {
    return action.payload;
  }

  if (typeof action?.payload?.message === 'string' && action.payload.message.trim()) {
    return action.payload.message;
  }

  if (typeof action?.payload?.data?.message === 'string' && action.payload.data.message.trim()) {
    return action.payload.data.message;
  }

  if (
    typeof action?.error?.message === 'string' &&
    action.error.message.trim() &&
    action.error.message !== 'Rejected'
  ) {
    return action.error.message;
  }

  return fallbackMessage;
};

/**
 * RTK slice definition: evaluationSlice.
 */
const evaluationSlice = createSlice({
  name: "evaluations",
  initialState,
  reducers: {
    /**
     * Reducer: clearError.
     */
    clearError: (state) => {
      state.error = null;
      state.studentEvaluationsError = null;
      state.studentEvaluationStatsError = null;
    },
    resetStudentEvaluationContext: (state) => resetLearnerScope(state, null),
    /**
     * Reducer: clearSearchResults.
     */
    clearSearchResults: (state) => {
      state.searchResults = {
        data: [],
        total: 0,
        totalPages: 0,
        currentPage: 1,
        searchTerm: ''
      };
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // ===== EXISTING EVALUATION CASES =====
      
      // Create Evaluation
      .addCase(createEvaluation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: createEvaluation.fulfilled
      .addCase(createEvaluation.fulfilled, (state, action) => {
        // Add the new evaluation with populated data to the beginning of the list
        if (action.payload && action.payload.studentId && action.payload.teacherId) {
          state.evaluations.unshift(action.payload);
        }
        state.loading = false;
        toast.success(t("store.evaluation.createdSuccessfully"));
      })
      // extraReducers handler: createEvaluation.rejected
      .addCase(createEvaluation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.createFailed"));
      })

      // Get All Evaluations
      .addCase(getAllEvaluations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getAllEvaluations.fulfilled
      .addCase(getAllEvaluations.fulfilled, (state, action) => {
        state.evaluations = action.payload?.evaluations || [];
        state.loading = false;
      })
      // extraReducers handler: getAllEvaluations.rejected
      .addCase(getAllEvaluations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.fetchFailed"));
      })

      // Learner loads require the exact live request AND learner/language scope.
      .addCase(getStudentEvaluations.pending, (state, action) => beginLearnerLoad(state, action, false))
      .addCase(getStudentEvaluations.fulfilled, (state, action) => {
        if (!currentLearnerLoad(state, action, false)) return;
        state.studentEvaluationsLoading = false;
        state.studentEvaluationsError = null;
        state.evaluations = action.payload?.evaluations || [];
        state.studentEvaluationPagination = {
          total: Number(action.payload?.total) || 0,
          currentPage: Number(action.payload?.page) || 1,
          totalPages: Number(action.payload?.pages) || 0,
          pageSize: Number(action.meta?.arg?.limit) || 10,
        };
        // Retain the latest ID; a completed/aborted request is never live again.
        syncLearnerLoad(state);
      })
      // extraReducers handler: getStudentEvaluations.rejected
      .addCase(getStudentEvaluations.rejected, (state, action) => {
        if (!currentLearnerLoad(state, action, false)) return;
        state.studentEvaluationsLoading = false;
        const error = normalizeStudentEvaluationError(action.payload || {});
        if (!action.meta.aborted && !action.meta.condition && error.kind !== 'aborted') {
          state.studentEvaluationsError = error;
          state.evaluations = [];
          state.studentEvaluationPagination = emptyStudentPagination();
          state.singleEvaluation = null;
          if (accessFailure(error)) state.stats = emptyStudentStats();
        }
        syncLearnerLoad(state);
      })

      // Get Teacher Evaluations
      .addCase(getTeacherEvaluations.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.teacherEvaluationsLoading = true;
        state.teacherEvaluationsError = null;
        state.teacherEvaluationsRequestId = action.meta?.requestId || null;
      })
      // extraReducers handler: getTeacherEvaluations.fulfilled
      .addCase(getTeacherEvaluations.fulfilled, (state, action) => {
        if (state.teacherEvaluationsRequestId && action.meta?.requestId !== state.teacherEvaluationsRequestId) {
          return;
        }
        state.evaluations = action.payload?.evaluations || [];
        state.loading = false;
        state.teacherEvaluationsLoading = false;
        state.teacherEvaluationsError = null;
        state.teacherEvaluationsRequestId = null;
      })
      // extraReducers handler: getTeacherEvaluations.rejected
      .addCase(getTeacherEvaluations.rejected, (state, action) => {
        if (state.teacherEvaluationsRequestId && action.meta?.requestId !== state.teacherEvaluationsRequestId) {
          return;
        }
        const errorMessage = getThunkErrorMessage(action, t("store.evaluation.fetchTeacherFailed"));
        state.loading = false;
        state.error = errorMessage;
        state.teacherEvaluationsLoading = false;
        state.teacherEvaluationsError = errorMessage;
        state.teacherEvaluationsRequestId = null;
        toast.error(errorMessage, { toastId: `teacher-evaluations:${errorMessage}` });
      })

      // Get Single Evaluation
      .addCase(getEvaluation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getEvaluation.fulfilled
      .addCase(getEvaluation.fulfilled, (state, action) => {
        state.singleEvaluation = action.payload?.evaluation || null;
        state.loading = false;
      })
      // extraReducers handler: getEvaluation.rejected
      .addCase(getEvaluation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.fetchSingleFailed"));
      })

      // Update Evaluation
      .addCase(updateEvaluation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: updateEvaluation.fulfilled
      .addCase(updateEvaluation.fulfilled, (state, action) => {
        const index = state.evaluations.findIndex(
          (evaluation) => evaluation._id === action.payload._id
        );
        if (index !== -1) {
          // Preserve existing populated studentId data if the update payload doesn't contain it
          const existingEvaluation = state.evaluations[index];
          const updatedEvaluation = {
            ...action.payload,
            // Preserve populated studentId if it exists in the current state and the payload has unpopulated data
            studentId: (action.payload.studentId && typeof action.payload.studentId === 'object' && action.payload.studentId.Username)
              ? action.payload.studentId
              : existingEvaluation.studentId || action.payload.studentId,
            // Preserve populated teacherId if it exists in the current state and the payload has unpopulated data  
            teacherId: (action.payload.teacherId && typeof action.payload.teacherId === 'object' && action.payload.teacherId.Username)
              ? action.payload.teacherId
              : existingEvaluation.teacherId || action.payload.teacherId
          };
          state.evaluations[index] = updatedEvaluation;
        }
        
        // Also update in admin evaluations if present
        const adminIndex = state.adminEvaluations.data.findIndex(
          (evaluation) => evaluation._id === action.payload._id
        );
        if (adminIndex !== -1) {
          const existingAdminEvaluation = state.adminEvaluations.data[adminIndex];
          const updatedAdminEvaluation = {
            ...action.payload,
            // Preserve populated studentId if it exists in the current state and the payload has unpopulated data
            studentId: (action.payload.studentId && typeof action.payload.studentId === 'object' && action.payload.studentId.Username)
              ? action.payload.studentId
              : existingAdminEvaluation.studentId || action.payload.studentId,
            // Preserve populated teacherId if it exists in the current state and the payload has unpopulated data
            teacherId: (action.payload.teacherId && typeof action.payload.teacherId === 'object' && action.payload.teacherId.Username)
              ? action.payload.teacherId
              : existingAdminEvaluation.teacherId || action.payload.teacherId
          };
          state.adminEvaluations.data[adminIndex] = updatedAdminEvaluation;
        }
        
        // Update singleEvaluation with preserved data
        if (state.singleEvaluation && state.singleEvaluation._id === action.payload._id) {
          state.singleEvaluation = {
            ...action.payload,
            studentId: (action.payload.studentId && typeof action.payload.studentId === 'object' && action.payload.studentId.Username)
              ? action.payload.studentId
              : state.singleEvaluation.studentId || action.payload.studentId,
            teacherId: (action.payload.teacherId && typeof action.payload.teacherId === 'object' && action.payload.teacherId.Username)
              ? action.payload.teacherId
              : state.singleEvaluation.teacherId || action.payload.teacherId
          };
        } else {
          state.singleEvaluation = action.payload;
        }
        
        state.loading = false;
        toast.success(t("store.evaluation.updatedSuccessfully"));
      })
      // extraReducers handler: updateEvaluation.rejected
      .addCase(updateEvaluation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.updateFailed"));
      })

      // Delete Evaluation
      .addCase(deleteEvaluation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: deleteEvaluation.fulfilled
      .addCase(deleteEvaluation.fulfilled, (state, action) => {
        state.evaluations = state.evaluations.filter(
          (evaluation) => evaluation._id !== action.payload.id
        );
        
        // Also remove from admin evaluations
        state.adminEvaluations.data = state.adminEvaluations.data.filter(
          (evaluation) => evaluation._id !== action.payload.id
        );
        
        state.loading = false;
        toast.success(action.payload.message || t("store.evaluation.deletedSuccessfully"));
      })
      // extraReducers handler: deleteEvaluation.rejected
      .addCase(deleteEvaluation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.deleteFailed"));
      })

      // Learner stats use the same context, without clearing a list denial.
      .addCase(getEvaluationStats.pending, (state, action) => beginLearnerLoad(state, action, true))
      // extraReducers handler: getEvaluationStats.fulfilled
      .addCase(getEvaluationStats.fulfilled, (state, action) => {
        if (!currentLearnerLoad(state, action, true)) return;
        state.studentEvaluationStatsLoading = false;
        state.studentEvaluationStatsError = null;
        state.stats = accessFailure(state.studentEvaluationsError) ? emptyStudentStats() : action.payload?.stats || emptyStudentStats();
        syncLearnerLoad(state);
      })
      // extraReducers handler: getEvaluationStats.rejected
      .addCase(getEvaluationStats.rejected, (state, action) => {
        if (!currentLearnerLoad(state, action, true)) return;
        state.studentEvaluationStatsLoading = false;
        const error = normalizeStudentEvaluationError(action.payload || {});
        if (!action.meta.aborted && !action.meta.condition && error.kind !== 'aborted') {
          state.studentEvaluationStatsError = error;
          state.stats = emptyStudentStats();
        }
        syncLearnerLoad(state);
      })

      // ===== NEW ADMIN EVALUATION CASES =====

      // Get All Evaluations Admin
      .addCase(getAllEvaluationsAdmin.pending, (state) => {
        state.adminLoading = true;
        state.error = null;
      })
      // extraReducers handler: getAllEvaluationsAdmin.fulfilled
      .addCase(getAllEvaluationsAdmin.fulfilled, (state, action) => {
        state.adminEvaluations = {
          data: action.payload?.evaluations || [],
          total: action.payload?.total || 0,
          totalPages: action.payload?.totalPages || 0,
          currentPage: action.payload?.currentPage || 1
        };
        state.adminLoading = false;
      })
      // extraReducers handler: getAllEvaluationsAdmin.rejected
      .addCase(getAllEvaluationsAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.fetchAdminFailed"));
      })

      // Search Evaluations
      .addCase(searchEvaluations.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      // extraReducers handler: searchEvaluations.fulfilled
      .addCase(searchEvaluations.fulfilled, (state, action) => {
        state.searchResults = {
          data: action.payload?.evaluations || [],
          total: action.payload?.total || 0,
          totalPages: action.payload?.totalPages || 0,
          currentPage: action.payload?.currentPage || 1,
          searchTerm: action.payload?.searchTerm || ''
        };
        state.searchLoading = false;
      })
      // extraReducers handler: searchEvaluations.rejected
      .addCase(searchEvaluations.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.searchFailed"));
      })

      // Get Evaluation Stats Admin
      .addCase(getEvaluationStatsAdmin.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      // extraReducers handler: getEvaluationStatsAdmin.fulfilled
      .addCase(getEvaluationStatsAdmin.fulfilled, (state, action) => {
        state.adminStats = {
          timeRange: action.payload?.timeRange || '30d',
          stats: action.payload?.stats || state.adminStats.stats
        };
        state.statsLoading = false;
      })
      // extraReducers handler: getEvaluationStatsAdmin.rejected
      .addCase(getEvaluationStatsAdmin.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.fetchAdminStatsFailed"));
      })

      // Get Evaluation Analytics
      .addCase(getEvaluationAnalytics.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      // extraReducers handler: getEvaluationAnalytics.fulfilled
      .addCase(getEvaluationAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload?.analytics || state.analytics;
        state.analyticsLoading = false;
      })
      // extraReducers handler: getEvaluationAnalytics.rejected
      .addCase(getEvaluationAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.fetchAnalyticsFailed"));
      })

      // Export Evaluations
      .addCase(exportEvaluations.pending, (state) => {
        state.exportLoading = true;
        state.error = null;
      })
      // extraReducers handler: exportEvaluations.fulfilled
      .addCase(exportEvaluations.fulfilled, (state, action) => {
        state.exportLoading = false;
        if (action.payload?.message) {
          toast.success(action.payload.message);
        } else {
          toast.success(t("store.evaluation.exportCompleted"));
        }
      })
      // extraReducers handler: exportEvaluations.rejected
      .addCase(exportEvaluations.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.exportFailed"));
      })

      // Bulk Delete Evaluations
      .addCase(bulkDeleteEvaluations.pending, (state) => {
        state.bulkDeleteLoading = true;
        state.error = null;
      })
      // extraReducers handler: bulkDeleteEvaluations.fulfilled
      .addCase(bulkDeleteEvaluations.fulfilled, (state, action) => {
        // Remove deleted evaluations from all relevant arrays
        const deletedIds = action.payload.evaluationIds;
        
        state.evaluations = state.evaluations.filter(
          (evaluation) => !deletedIds.includes(evaluation._id)
        );
        
        state.adminEvaluations.data = state.adminEvaluations.data.filter(
          (evaluation) => !deletedIds.includes(evaluation._id)
        );
        
        state.searchResults.data = state.searchResults.data.filter(
          (evaluation) => !deletedIds.includes(evaluation._id)
        );
        
        state.bulkDeleteLoading = false;
        toast.success(action.payload.message || t("store.evaluation.bulkDeletedSuccessfully"));
      })
      // extraReducers handler: bulkDeleteEvaluations.rejected
      .addCase(bulkDeleteEvaluations.rejected, (state, action) => {
        state.bulkDeleteLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.bulkDeleteFailed"));
      })

      // ===== TRIAL PREFERENCES CASES =====

      // Get All Trial Preferences
      .addCase(getAllTrialPreferences.pending, (state) => {
        state.trialLoading = true;
        state.error = null;
      })
      // extraReducers handler: getAllTrialPreferences.fulfilled
      .addCase(getAllTrialPreferences.fulfilled, (state, action) => {
        state.trialPreferences = {
          data: action.payload?.preferences || [],
          total: action.payload?.total || 0,
          totalPages: action.payload?.totalPages || 0,
          currentPage: action.payload?.currentPage || 1
        };
        state.trialLoading = false;
      })
      // extraReducers handler: getAllTrialPreferences.rejected
      .addCase(getAllTrialPreferences.rejected, (state, action) => {
        state.trialLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.fetchTrialPrefsFailed"));
      })

      // Update Trial Preferences
      .addCase(updateTrialPreferences.pending, (state) => {
        state.trialLoading = true;
        state.error = null;
      })
      // extraReducers handler: updateTrialPreferences.fulfilled
      .addCase(updateTrialPreferences.fulfilled, (state, action) => {
        const index = state.trialPreferences.data.findIndex(
          (pref) => pref._id === action.payload._id
        );
        if (index !== -1) {
          state.trialPreferences.data[index] = action.payload;
        }
        state.trialLoading = false;
        toast.success(t("store.evaluation.trialPrefsUpdated"));
      })
      // extraReducers handler: updateTrialPreferences.rejected
      .addCase(updateTrialPreferences.rejected, (state, action) => {
        state.trialLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.trialPrefsUpdateFailed"));
      })

      // Delete Trial Preferences
      .addCase(deleteTrialPreferences.pending, (state) => {
        state.trialLoading = true;
        state.error = null;
      })
      // extraReducers handler: deleteTrialPreferences.fulfilled
      .addCase(deleteTrialPreferences.fulfilled, (state, action) => {
        state.trialPreferences.data = state.trialPreferences.data.filter(
          (pref) => pref._id !== action.payload.id
        );
        state.trialLoading = false;
        toast.success(action.payload.message || t("store.evaluation.trialPrefsDeleted"));
      })
      // extraReducers handler: deleteTrialPreferences.rejected
      .addCase(deleteTrialPreferences.rejected, (state, action) => {
        state.trialLoading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.evaluation.trialPrefsDeleteFailed"));
      });
  },
});

export const { clearError, clearSearchResults, resetStudentEvaluationContext } = evaluationSlice.actions;
export default evaluationSlice.reducer;
